import { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Box,
    CircularProgress,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { getDocumentStatusMoves, getDocumentStatusMovesByPerson } from 'api/rest/analytics';
import { AppDocStatus } from 'api/models/AppDocStatus';
import { formatLocalizedShortDate } from 'utils/formatDate';
import useAnalyticsData from '../../hooks/useAnalyticsData';
import type {
    DocumentStatusMovesSummaryData,
    DocumentStatusMovesDrilldownData,
    DocumentStatusMoveDetail,
    DocumentStatusMoveAgentSummary,
} from 'hooks/analytics';
import {
    AnalyticsColors,
    SectionCard,
    SectionTitle,
    stateMessageSx,
    loadingWrapSx,
    thSx,
    tdSx,
    trHoverSx,
} from '../../components/analyticsUi';
import { OPERATIONS_CASE_STATUS_TRACKING } from '../../constants/metricDescriptions';
import InfoTooltip from 'components/v2/common/infoTooltip';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type SortDirection = 'asc' | 'desc';

const SortIndicator = ({ direction }: { direction: SortDirection }) => (
    <Box
        component="span"
        sx={{ fontSize: 10, lineHeight: 1, color: AnalyticsColors.primary, ml: '4px' }}
    >
        {direction === 'asc' ? '▲' : '▼'}
    </Box>
);

const STATUS_LABELS: Record<string, string> = {
    [AppDocStatus.MISSING]: 'Missing',
    [AppDocStatus.FIX_REQUIRED]: 'Fix Required',
    [AppDocStatus.WAITING_FOR_APPROVAL]: 'Waiting for Approval',
    [AppDocStatus.SIGNATURE_REQUIRED]: 'Signature Required',
    [AppDocStatus.PREAPPROVED]: 'Preapproved',
    [AppDocStatus.APPROVED]: 'Approved',
    [AppDocStatus.AUTO_FILLED]: 'Auto Filled',
    [AppDocStatus.SENT_TO_CLIENT]: 'Sent to Client',
    [AppDocStatus.CLIENT_COMPLETED]: 'Client Completed',
    [AppDocStatus.PRE_APPROVED]: 'Pre-Approved',
};

const formatStatusLabel = (status: string | null | undefined) => {
    if (!status) return '—';
    return STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
};

const kpiCardSx = {
    flex: '1 1 160px',
    background: AnalyticsColors.primaryLight,
    borderRadius: '10px',
    p: '1.25rem 1.5rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
};

const KpiCard = ({ label, value }: { label: string; value: number }) => (
    <Box sx={kpiCardSx}>
        <Typography
            sx={{
                fontSize: 12,
                fontWeight: 600,
                color: AnalyticsColors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: 28,
                fontWeight: 700,
                color: AnalyticsColors.primary,
                fontVariantNumeric: 'tabular-nums',
            }}
        >
            {value.toLocaleString()}
        </Typography>
    </Box>
);

const resolvePersons = (data: DocumentStatusMovesSummaryData): DocumentStatusMoveAgentSummary[] =>
    data.persons ?? data.agents ?? [];

const resolvePersonId = (person: DocumentStatusMoveAgentSummary) =>
    person.personId ?? person.agentId ?? 0;

const resolvePersonName = (person: DocumentStatusMoveAgentSummary) =>
    person.personName ?? person.agentName ?? 'Unknown';

const resolvePersonEmail = (person: DocumentStatusMoveAgentSummary) =>
    person.personEmail ?? person.agentEmail ?? '';

// ---------------------------------------------------------------------------
// Person summary table (Fix Required / Pre-Approved focus)
// ---------------------------------------------------------------------------

type PersonSortKey =
    | 'personName'
    | 'personEmail'
    | 'totalStatusMoves'
    | 'fixRequiredCount'
    | 'preapprovedCount';

interface PersonTableProps {
    data: DocumentStatusMovesSummaryData;
    onPersonClick: (personId: number, personName: string) => void;
}

const PersonSummaryTable = ({ data, onPersonClick }: PersonTableProps) => {
    const persons = resolvePersons(data);
    const [sortKey, setSortKey] = useState<PersonSortKey>('totalStatusMoves');
    const [sortDir, setSortDir] = useState<SortDirection>('desc');

    const handleSort = (key: PersonSortKey) => {
        if (key === sortKey) {
            setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortKey(key);
            setSortDir(
                key === 'totalStatusMoves' ||
                    key === 'fixRequiredCount' ||
                    key === 'preapprovedCount'
                    ? 'desc'
                    : 'asc'
            );
        }
    };

    const sorted = useMemo(() => {
        const copy = [...persons];
        copy.sort((a, b) => {
            let diff = 0;
            if (sortKey === 'personName') {
                diff = resolvePersonName(a).localeCompare(resolvePersonName(b));
            } else if (sortKey === 'personEmail') {
                diff = resolvePersonEmail(a).localeCompare(resolvePersonEmail(b));
            } else {
                diff = (a[sortKey] ?? 0) - (b[sortKey] ?? 0);
            }
            return sortDir === 'desc' ? -diff : diff;
        });
        return copy;
    }, [persons, sortKey, sortDir]);

    const renderTh = (label: string, key: PersonSortKey, align?: 'left' | 'right') => (
        <Box
            component="th"
            sx={thSx({ align: align ?? 'left', sortable: true, active: sortKey === key })}
            onClick={() => handleSort(key)}
            style={{ cursor: 'pointer' }}
        >
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '2px',
                    justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
                    width: '100%',
                }}
            >
                {label}
                {sortKey === key && <SortIndicator direction={sortDir} />}
            </Box>
        </Box>
    );

    const personCount = data.summary.personCount ?? data.summary.agentCount ?? persons.length;

    return (
        <>
            <Box sx={{ display: 'flex', gap: '1rem', mb: '1.5rem', flexWrap: 'wrap' }}>
                <KpiCard label="Total Status Moves" value={data.summary.totalStatusMoves} />
                <KpiCard label="People" value={personCount} />
                <KpiCard label="Fix Required" value={data.summary.fixRequiredCount} />
                <KpiCard label="Pre-Approved" value={data.summary.preapprovedCount} />
            </Box>

            {sorted.length === 0 ? (
                <Box sx={stateMessageSx}>No document status changes for this period.</Box>
            ) : (
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            {renderTh('Person', 'personName')}
                            {renderTh('Email', 'personEmail')}
                            {renderTh('All Status Moves', 'totalStatusMoves', 'right')}
                            {renderTh('Fix Required', 'fixRequiredCount', 'right')}
                            {renderTh('Pre-Approved', 'preapprovedCount', 'right')}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((person) => {
                            const personId = resolvePersonId(person);
                            const personName = resolvePersonName(person);
                            return (
                                <Box
                                    component="tr"
                                    key={personId}
                                    sx={{
                                        ...trHoverSx,
                                        cursor: 'pointer',
                                        '&:hover': { background: AnalyticsColors.primaryLight },
                                    }}
                                    onClick={() => onPersonClick(personId, personName)}
                                >
                                    <Box component="td" sx={tdSx()}>
                                        <Box
                                            component="span"
                                            sx={{
                                                fontWeight: 600,
                                                color: AnalyticsColors.primary,
                                            }}
                                        >
                                            {personName}
                                        </Box>
                                    </Box>
                                    <Box component="td" sx={tdSx()}>
                                        {resolvePersonEmail(person)}
                                    </Box>
                                    <Box component="td" sx={tdSx({ align: 'right', bold: true })}>
                                        {person.totalStatusMoves.toLocaleString()}
                                    </Box>
                                    <Box component="td" sx={tdSx({ align: 'right' })}>
                                        {person.fixRequiredCount.toLocaleString()}
                                    </Box>
                                    <Box component="td" sx={tdSx({ align: 'right' })}>
                                        {person.preapprovedCount.toLocaleString()}
                                    </Box>
                                </Box>
                            );
                        })}
                    </tbody>
                </Box>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Detail modal
// ---------------------------------------------------------------------------

type DetailSortKey =
    | 'documentName'
    | 'clientName'
    | 'programName'
    | 'statusFrom'
    | 'statusTo'
    | 'occurredAt';

interface DetailModalProps {
    open: boolean;
    personName: string;
    loading: boolean;
    error: string | null;
    data: DocumentStatusMovesDrilldownData | null;
    onClose: () => void;
}

const PersonDetailModal = ({
    open,
    personName,
    loading,
    error,
    data,
    onClose,
}: DetailModalProps) => {
    const [sortKey, setSortKey] = useState<DetailSortKey>('occurredAt');
    const [sortDir, setSortDir] = useState<SortDirection>('desc');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const moves: DocumentStatusMoveDetail[] = data?.moves ?? data?.documents ?? [];
    const total = data?.totalMoves ?? data?.totalDocuments ?? moves.length;
    const email =
        data?.person?.personEmail ??
        data?.person?.agentEmail ??
        data?.agent?.agentEmail ??
        data?.agent?.personEmail ??
        '—';

    const handleSort = (key: DetailSortKey) => {
        if (key === sortKey) {
            setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortKey(key);
            setSortDir(key === 'occurredAt' ? 'desc' : 'asc');
        }
    };

    const sorted = useMemo(() => {
        const copy = [...moves];
        copy.sort((a, b) => {
            let diff = 0;
            if (sortKey === 'occurredAt') {
                diff = (a.occurredAt ?? '').localeCompare(b.occurredAt ?? '');
            } else if (sortKey === 'statusFrom' || sortKey === 'statusTo') {
                const aVal =
                    sortKey === 'statusTo' ? (a.statusTo ?? a.status ?? '') : (a.statusFrom ?? '');
                const bVal =
                    sortKey === 'statusTo' ? (b.statusTo ?? b.status ?? '') : (b.statusFrom ?? '');
                diff = aVal.localeCompare(bVal);
            } else {
                diff = (a[sortKey] ?? '').localeCompare(b[sortKey] ?? '');
            }
            return sortDir === 'desc' ? -diff : diff;
        });
        return copy;
    }, [moves, sortKey, sortDir]);

    const renderTh = (label: string, key: DetailSortKey) => (
        <Box
            component="th"
            sx={thSx({ sortable: true, active: sortKey === key })}
            onClick={() => handleSort(key)}
            style={{ cursor: 'pointer' }}
        >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                {label}
                {sortKey === key && <SortIndicator direction={sortDir} />}
            </Box>
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pr: 1,
                    pb: 1,
                }}
            >
                <Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                        {personName ||
                            data?.person?.personName ||
                            data?.agent?.agentName ||
                            'Person'}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: AnalyticsColors.textSecondary }}>
                        {email}
                        {` · ${total.toLocaleString()} status move${total !== 1 ? 's' : ''}`}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" aria-label="Close">
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 2, pb: 2 }}>
                {loading ? (
                    <Box sx={loadingWrapSx}>
                        <CircularProgress size={28} />
                    </Box>
                ) : error ? (
                    <Box sx={stateMessageSx}>{error}</Box>
                ) : sorted.length === 0 ? (
                    <Box sx={stateMessageSx}>No document status changes for this person.</Box>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box
                            component="table"
                            sx={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                minWidth: 900,
                            }}
                        >
                            <thead>
                                <tr>
                                    {renderTh('Document', 'documentName')}
                                    {renderTh('Client', 'clientName')}
                                    {renderTh('Program', 'programName')}
                                    {renderTh('From', 'statusFrom')}
                                    {renderTh('To', 'statusTo')}
                                    {renderTh('Changed At', 'occurredAt')}
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((move) => {
                                    const statusTo = move.statusTo ?? move.status;
                                    return (
                                        <Box component="tr" key={move.eventId} sx={trHoverSx}>
                                            <Box component="td" sx={tdSx()}>
                                                {move.documentName ?? '—'}
                                            </Box>
                                            <Box component="td" sx={tdSx()}>
                                                {move.clientName ?? '—'}
                                            </Box>
                                            <Box component="td" sx={tdSx()}>
                                                {move.programName ?? '—'}
                                            </Box>
                                            <Box component="td" sx={tdSx()}>
                                                {formatStatusLabel(move.statusFrom)}
                                            </Box>
                                            <Box component="td" sx={tdSx()}>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color:
                                                            statusTo === AppDocStatus.FIX_REQUIRED
                                                                ? AnalyticsColors.warning
                                                                : statusTo ===
                                                                        AppDocStatus.PREAPPROVED ||
                                                                    statusTo ===
                                                                        AppDocStatus.PRE_APPROVED
                                                                  ? AnalyticsColors.success
                                                                  : AnalyticsColors.textPrimary,
                                                    }}
                                                >
                                                    {formatStatusLabel(statusTo)}
                                                </Box>
                                            </Box>
                                            <Box component="td" sx={tdSx()}>
                                                {formatLocalizedShortDate(move.occurredAt, {
                                                    includeTime: true,
                                                })}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </tbody>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Main view — Case Status Tracking (previous feature)
// ---------------------------------------------------------------------------

interface CaseStatusTrackingProps {
    from?: string;
    to?: string;
    serviceProviderId?: string;
}

const CaseStatusTracking = ({ from, to, serviceProviderId }: CaseStatusTrackingProps) => {
    const [selectedPerson, setSelectedPerson] = useState<{
        id: number;
        name: string;
    } | null>(null);
    const [detailData, setDetailData] = useState<DocumentStatusMovesDrilldownData | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState<string | null>(null);

    const summary = useAnalyticsData<DocumentStatusMovesSummaryData>(
        () => getDocumentStatusMoves(from, to, serviceProviderId),
        [from, to, serviceProviderId]
    );

    useEffect(() => {
        if (!selectedPerson) {
            setDetailData(null);
            setDetailError(null);
            setDetailLoading(false);
            return undefined;
        }

        let cancelled = false;
        setDetailLoading(true);
        setDetailError(null);
        setDetailData(null);

        getDocumentStatusMovesByPerson(selectedPerson.id, from, to, serviceProviderId)
            .then((res) => {
                if (!cancelled) setDetailData(res.data);
            })
            .catch((err) => {
                if (!cancelled) {
                    setDetailError(
                        err?.response?.data?.message ||
                            err.message ||
                            'Failed to load status move details.'
                    );
                }
            })
            .finally(() => {
                if (!cancelled) setDetailLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPerson?.id, from, to, serviceProviderId]);

    const handlePersonClick = useCallback((personId: number, personName: string) => {
        setSelectedPerson({ id: personId, name: personName });
    }, []);

    const handleCloseModal = useCallback(() => {
        setSelectedPerson(null);
    }, []);

    return (
        <SectionCard>
            <SectionTitle>
                Case Status Tracking
                <InfoTooltip title={OPERATIONS_CASE_STATUS_TRACKING} />
            </SectionTitle>

            {summary.loading ? (
                <Box sx={loadingWrapSx}>
                    <CircularProgress size={28} />
                </Box>
            ) : summary.error ? (
                <Box sx={stateMessageSx}>{summary.error}</Box>
            ) : summary.data ? (
                <PersonSummaryTable data={summary.data} onPersonClick={handlePersonClick} />
            ) : null}

            <PersonDetailModal
                open={selectedPerson !== null}
                personName={selectedPerson?.name ?? ''}
                loading={detailLoading}
                error={detailError}
                data={detailData}
                onClose={handleCloseModal}
            />
        </SectionCard>
    );
};

export default CaseStatusTracking;
